/**
 * useBuddyGroup — Fetch a single buddy group detail with members.
 *
 * Merges accepted members (from buddy_group_members) with pending
 * invitations (from buddy_group_invitations) into a single member list.
 * Pending invitations show with invitation_status='pending' so the UI
 * can render them with status badges.
 */

import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../api/http-client';
import { buddyGroupOpsKeys } from '../api/extensions/useBuddyGroupOps';
import { getMyGroup, getBuddyGroupMembers } from '../services/buddy-group.service';
import type {
  BuddyGroupDetail,
  BuddyGroupMemberView,
  BuddyGroupStatus,
  DecoupledPartneredEntry,
} from '../types/buddy-groups';

/** Extended query keys for single-group detail. */
export const buddyGroupDetailKeys = {
  detail: (groupId: string) =>
    [...buddyGroupOpsKeys.all, 'detail', groupId] as const,
};

/** Safely cast a string to BuddyGroupStatus, returning null if invalid. */
function parseBuddyGroupStatus(value: unknown): BuddyGroupStatus | null {
  const valid: BuddyGroupStatus[] = ['pending', 'active', 'locked', 'disbanded'];
  return typeof value === 'string' && valid.includes(value as BuddyGroupStatus)
    ? (value as BuddyGroupStatus)
    : null;
}

/** Fetch pending invitations for a group via CRUD and enrich with profile names. */
async function fetchPendingInvitations(groupId: string): Promise<BuddyGroupMemberView[]> {
  try {
    const res = await httpClient.get<{
      data: Array<{
        id: string;
        buddy_group_id: string;
        invitee_user_profile_id: string;
        status: string;
      }>;
    }>(`/buddy-group-invitations?buddy_group_id=${groupId}&status=pending&limit=20`);

    const pending = (res?.data ?? []).filter((inv) => inv.status === 'pending');
    if (pending.length === 0) return [];

    // Enrich with display names ONLY. We deliberately do NOT pull the
    // invitee's email here: until they accept the invitation, their
    // email address must stay hidden from the group's existing members.
    // Accepting the invite is the trust gesture that opts them into
    // email visibility.
    return Promise.all(
      pending.map(async (inv) => {
        let displayName: string | null = null;
        try {
          const profileRes = await httpClient.get<{
            data: {
              display_first_name?: string | null;
              display_last_name?: string | null;
              first_name?: string | null;
              last_name?: string | null;
            };
          }>(`/user-profiles/${inv.invitee_user_profile_id}`);
          const p = profileRes?.data;
          const first = p?.display_first_name ?? p?.first_name ?? '';
          const last = p?.display_last_name ?? p?.last_name ?? '';
          displayName = [first, last].filter(Boolean).join(' ') || null;
        } catch {
          // Profile lookup failed
        }

        return {
          id: inv.id,
          user_profile_id: inv.invitee_user_profile_id,
          is_primary: false,
          display_name: displayName,
          member_number: null,
          email: null,
          invitation_status: 'pending' as const,
          invitation_id: inv.id,
        };
      })
    );
  } catch {
    return [];
  }
}

export function useBuddyGroup(groupId: string) {
  return useQuery<BuddyGroupDetail | null>({
    queryKey: buddyGroupDetailKeys.detail(groupId),
    queryFn: async (): Promise<BuddyGroupDetail | null> => {
      if (!groupId) return null;

      // Step 1: Fetch group base record from CRUD
      const crudResponse = await httpClient.get<{
        data: {
          id: string;
          group_name: string | null;
          rodeo_id: string | null;
          status?: string | null;
          primary_member_user_profile_id?: string | null;
        };
        success: boolean;
      }>(`/buddy-groups/${groupId}`);

      const group = crudResponse?.data;
      if (!group) return null;

      // Step 2: Try the workflow endpoint for enriched data
      let baseMembers: BuddyGroupMemberView[] = [];
      let resolvedStatus: BuddyGroupStatus = 'pending';
      let resolvedPrimary: string | null = null;
      let resolvedPreferencesFirst: string | null = null;
      let resolvedPreferencesSecond: string | null = null;
      let resolvedPreferencesLockedAt: string | null = null;
      let resolvedDecoupledEntries: DecoupledPartneredEntry[] = [];

      if (group.rodeo_id) {
        try {
          const enriched = await getMyGroup(group.rodeo_id);
          if (enriched && enriched.id === groupId) {
            baseMembers = enriched.members.map((m) => ({
              ...m,
              invitation_status: 'accepted' as const,
            }));
            resolvedStatus = enriched.status;
            resolvedPrimary = enriched.primary_member_user_profile_id;
            resolvedPreferencesFirst = enriched.preferences_first ?? null;
            resolvedPreferencesSecond = enriched.preferences_second ?? null;
            resolvedPreferencesLockedAt = enriched.preferences_locked_at ?? null;
            resolvedDecoupledEntries = enriched.decoupled_partnered_entries ?? [];
          }
        } catch {
          // Fall through to CRUD assembly
        }
      }

      // Step 3: Fallback — assemble from CRUD if workflow didn't return data
      if (baseMembers.length === 0) {
        const memberRecords = await getBuddyGroupMembers(groupId);
        const primaryMember = memberRecords.find((m) => m.is_primary);

        baseMembers = await Promise.all(
          memberRecords.map(async (m) => {
            let displayName: string | null = null;
            let email: string | null = null;
            try {
              const profileRes = await httpClient.get<{
                data: {
                  display_first_name?: string | null;
                  display_last_name?: string | null;
                  first_name?: string | null;
                  last_name?: string | null;
                  primary_email?: string | null;
                };
              }>(`/user-profiles/${m.user_profile_id}`);
              const p = profileRes?.data;
              const first = p?.display_first_name ?? p?.first_name ?? '';
              const last = p?.display_last_name ?? p?.last_name ?? '';
              displayName = [first, last].filter(Boolean).join(' ') || null;
              email = p?.primary_email ?? null;
            } catch {
              // Profile lookup failed
            }

            return {
              id: m.id,
              user_profile_id: m.user_profile_id,
              is_primary: m.is_primary ?? false,
              display_name: displayName,
              member_number: null,
              email,
              invitation_status: 'accepted' as const,
            };
          })
        );

        resolvedStatus = parseBuddyGroupStatus(group.status) ?? 'pending';
        resolvedPrimary =
          group.primary_member_user_profile_id ??
          primaryMember?.user_profile_id ??
          null;
      }

      // Step 4: Fetch pending invitations and merge
      const pendingMembers = await fetchPendingInvitations(groupId);
      const existingIds = new Set(baseMembers.map((m) => m.user_profile_id));
      const uniquePending = pendingMembers.filter(
        (p) => !existingIds.has(p.user_profile_id)
      );
      const allMembers = [...baseMembers, ...uniquePending];

      return {
        id: group.id,
        group_name: group.group_name,
        rodeo_id: group.rodeo_id ?? null,
        primary_member_user_profile_id: resolvedPrimary,
        status: resolvedStatus,
        preferences_first: resolvedPreferencesFirst,
        preferences_second: resolvedPreferencesSecond,
        preferences_locked_at: resolvedPreferencesLockedAt,
        members: allMembers,
        decoupled_partnered_entries: resolvedDecoupledEntries,
      };
    },
    enabled: !!groupId,
    staleTime: 30 * 1000,
  });
}
